import { useState, useEffect } from "react"
import { FiSave, FiX } from "react-icons/fi"
import { SingleValue } from "react-select"
import CustomSelect, {
  SelectOption,
} from "../../component/customSelect/customSelect"
import Button from "../../component/button/button"
import { complexities } from "../../utils/complexity"
import {
  findTaxonomySelectOption,
  taxonomyToSelectOptions,
} from "../../utils/subjectTaxonomy"
import { PreviewSubjectOption, ProcessedQuestionContent } from "./types"

interface QuestionEditFormProps {
  content: ProcessedQuestionContent
  subjects: PreviewSubjectOption[]
  onSave: () => void
  onCancel: () => void
  onChange: (name: string, value: unknown) => void
  validationError?: string | null
}

const QuestionEditForm = ({
  content,
  subjects,
  onSave,
  onCancel,
  onChange,
  validationError,
}: QuestionEditFormProps) => {
  const [topicOptions, setTopicOptions] = useState<SelectOption[]>([])
  const [localTopicError, setLocalTopicError] = useState<string | null>(null)

  useEffect(() => {
    const selectedSubject = subjects.find(
      (item) => item.value === content.subject,
    )
    setTopicOptions(taxonomyToSelectOptions(selectedSubject?.topics ?? []))
  }, [content.subject, subjects])

  const handleSaveClick = () => {
    if (!content.topic) {
      setLocalTopicError("Chapter/Topic is required.")
      return
    }
    setLocalTopicError(null)
    onSave()
  }

  const handleCancelClick = () => {
    setLocalTopicError(null)
    onCancel()
  }

  return (
    <div className='right'>
      <h3>Question Details</h3>

      <div className='select subject_select' style={{ marginBottom: 15 }}>
        <label>
          Subject <span>*</span>
        </label>
        <CustomSelect<PreviewSubjectOption>
          placeholder='Select Subject'
          data={subjects}
          onChange={(value: SingleValue<PreviewSubjectOption>) => {
            onChange("subject", value)
            setLocalTopicError(null)
          }}
          value={
            subjects.find((item) => item.value === content.subject) ?? null
          }
        />
      </div>

      <div className='select topic_select' style={{ marginBottom: 15 }}>
        <label>
          Chapter/Topic <span>*</span>
        </label>
        <CustomSelect
          placeholder='Select Topic'
          data={topicOptions}
          onChange={(value: SingleValue<SelectOption>) => {
            onChange("topic", value)
            setLocalTopicError(null)
          }}
          value={
            findTaxonomySelectOption(
              topicOptions,
              content.topic,
              content.topic,
            )
          }
        />
        {(localTopicError || validationError) && (
          <p className='error_message'>{localTopicError || validationError}</p>
        )}
      </div>

      <div className='select topic_select'>
        <label>
          Complexity <span>*</span>
        </label>
        <CustomSelect
          placeholder='Select Complexity'
          data={complexities}
          onChange={(value: SingleValue<SelectOption>) =>
            onChange("complexity", value)
          }
          value={
            complexities.find((item) => item.value === content.complexity) ??
            null
          }
        />
      </div>

      <div
        className='button_container'
        style={{
          marginTop: "auto",
          justifyContent: "center",
          display: "flex",
          gap: 10,
        }}
      >
        <Button
          text='Save'
          type='secondary'
          icon={<FiSave />}
          style={{
            width: "fit-content",
            height: 40,
            gap: 5,
            fontSize: 14,
            padding: "8px 12px",
            marginTop: "auto",
            marginLeft: "auto",
          }}
          onClick={handleSaveClick}
        />
        <Button
          text='Cancel'
          type='link'
          icon={<FiX />}
          style={{
            width: "fit-content",
            height: 40,
            gap: 5,
            fontSize: 14,
            padding: "8px 12px",
            marginTop: "auto",
            color: "var(--brand-red-800)",
          }}
          onClick={handleCancelClick}
        />
      </div>
    </div>
  )
}

export default QuestionEditForm
